import type { EventHint } from '@sentry/browser';

// Apollo throws one identical `ServerError: ... status code 400` for every
// rejected document, so the default fingerprint (type + message + the three
// throwServerError frames) collapses unrelated schema breakages into one issue.
const VALIDATION_CODE = 'GRAPHQL_VALIDATION_FAILED';

// An identifier or a dotted coordinate such as `Query.tradingPermissions`.
const COORDINATE = '([A-Za-z0-9_.]{1,64})';

// Bounds the grouping key when one document fails validation many times over.
const MAX_COORDINATES = 4;

// Only shapes whose quoted tokens are schema names are read. Other validation
// messages quote document literals — `Expected value of type "Int", found
// "abc".`, `Value "FOO" does not exist in "Bar" enum.` — and a literal must
// never become a grouping key: a fingerprint is as AI-visible as `extra` and
// gets no pass from sanitizeEvent, and a per-value key would fork an issue per
// value. So this is an allowlist by shape; anything unmatched groups under one
// `unparsed` bucket rather than having its quoted tokens read loosely. Anchored
// and terminated, so a trailing ` Did you mean "a", or "b"?` (graphql-js quotes
// up to 5 suggestions) falls outside the match.
const COORDINATE_SHAPES = [
  // FieldsOnCorrectType — field removed or renamed
  new RegExp(`^Cannot query field "${COORDINATE}" on type "${COORDINATE}"\\.`),
  // KnownArgumentNames — argument removed or renamed
  new RegExp(`^Unknown argument "${COORDINATE}" on field "${COORDINATE}"\\.`),
  // KnownTypeNames / KnownDirectives / KnownFragmentNames
  new RegExp(`^Unknown (?:type|directive|fragment) "@?${COORDINATE}"\\.`),
  // ProvidedRequiredArguments — argument became required
  new RegExp(
    `^Field "${COORDINATE}" argument "${COORDINATE}" of type "[^"]{1,64}" is required`,
  ),
];

type ServerErrorBody = {
  result?: {
    errors?: {
      message?: unknown;
      extensions?: { code?: unknown };
    }[];
  };
};

/**
 * Groups a GraphQL validation failure by the schema coordinates it names, so
 * each breaking schema change gets its own Sentry issue.
 *
 * Returns `undefined` for anything else, leaving the default fingerprint in
 * place — including a 400 whose body carries no `GRAPHQL_VALIDATION_FAILED`.
 */
export const gqlValidationFingerprint = (
  err: EventHint['originalException'],
): string[] | undefined => {
  const errors = (err as ServerErrorBody | null | undefined)?.result?.errors;

  if (!Array.isArray(errors)) {
    return undefined;
  }

  const messages = errors
    .filter((gqlError) => gqlError?.extensions?.code === VALIDATION_CODE)
    .map((gqlError) => gqlError.message)
    .filter((message): message is string => typeof message === 'string');

  if (!messages.length) {
    return undefined;
  }

  // Sorted so the order of a multi-error response can't split one group.
  const coordinates = new Set<string>();
  for (const message of messages) {
    for (const shape of COORDINATE_SHAPES) {
      const match = shape.exec(message);

      if (match) {
        for (const coordinate of match.slice(1)) {
          coordinates.add(coordinate);
        }
        break;
      }
    }
  }

  const key = [...coordinates].sort().slice(0, MAX_COORDINATES);

  return ['gql-validation-failed', ...(key.length ? key : ['unparsed'])];
};
